import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import type BoardLoop from 'webapp/models-custom/board-loop';

import type {
	TrackingOriginType,
	TrackingOriginTypeForDuplicate,
} from 'webapp/services/model-manager/board-loops';

export default class BoardLoopsController extends Controller {
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('popup') declare popupService: Service.Popup;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;

	trackingOrigin: TrackingOriginType = 'loops';

	@tracked isRecentlyDeletedFilterActive = false;

	get boardLoops(): BoardLoop[] {
		return this.mmBoardLoopsService.normalBoardLoopModelsWithDeleted;
	}

	get hasLoops(): boolean {
		return this.boardLoops.length > 0;
	}

	get hideRecommendedTemplates() {
		return this.boardLoops.length >= 3 || this.isRecentlyDeletedFilterActive;
	}

	get boardLoopsEnabled() {
		return this.billingService.boardLoopsEnabled;
	}

	@action
	createNew(): void {
		this.mmBoardLoopsService.createNewWithTracking();
	}

	@action
	openPreview(model: BoardLoop, boardLoops: BoardLoop[]): void {
		this.mmBoardLoopsService.openPreviewPopup(model, 'loops', boardLoops);
	}

	@action
	edit(model: BoardLoop): void {
		this.mmBoardLoopsService.openEditPopup(model);
	}

	@action
	toggleFavorite(model: BoardLoop): void {
		this.mmBoardLoopsService.toggleFavorite(model);
	}

	@action
	share(model: BoardLoop): void {
		this.mmBoardLoopsService.openSharePopup(model, this.trackingOrigin);
	}

	@action
	openFullscreenView(model: BoardLoop): void {
		this.mmBoardLoopsService.openFullscreenView(model);
	}

	@action
	duplicate(model: BoardLoop): void {
		void this.mmBoardLoopsService
			.duplicate(model, this.trackingOrigin as TrackingOriginTypeForDuplicate)
			.then((copiedLoopRecord) => {
				const copiedLoopModel = this.mmBoardLoopsService.createModel(copiedLoopRecord);
				this.mmBoardLoopsService.openPreviewPopup(copiedLoopModel, 'loops');
			});
	}

	@action
	openInDesigner(model: BoardLoop): void {
		this.mmBoardLoopsService.openInDesigner(model);
	}

	@action
	saveAsTemplate(model: BoardLoop): void {
		this.mmBoardLoopsService.openAccountTemplateEditPopup(model, 'board-loops');
	}

	@action
	createReport(model: BoardLoop): void {
		this.mmReportsService.createFromLoop(model, 'loops');
	}

	@action
	delete(model: BoardLoop): void {
		this.mmBoardLoopsService.delete(model);
	}

	@action
	restore(model: BoardLoop): void {
		void this.mmBoardLoopsService.restore(model);
	}

	@action
	deletePermanently(model: BoardLoop, onPermanentDelete?: () => void): void {
		this.mmBoardLoopsService.delete(
			model,
			onPermanentDelete
				? () => {
						onPermanentDelete();
						return 0;
					}
				: undefined,
			true,
		);
	}

	@action
	updateRecentlyDeletedFilter(isActive: boolean): void {
		this.isRecentlyDeletedFilterActive = isActive;
	}
}
